import React from "react";
import { MdOpenInNew } from "react-icons/md";
import { FaQuestionCircle } from "react-icons/fa";

const ChangeIndicator = ({ change }) => {
  if (!change) return null;
  return (
    <span className="inline-flex items-center gap-1 ml-2">
      <div className="relative group">
        <FaQuestionCircle className="text-green-500 h-4 w-4 cursor-help" />
        <div className="absolute left-full bottom-full w-[150px] mb-2 hidden group-hover:block bg-gray-800 text-white text-xs py-1 px-2 rounded shadow-lg z-50 max-w-[600px] break-words transform -translate-x-1/2">
          From: <span className="text-red-500">{change.from || "None"}</span> To: <span className="text-green-500">{change.to || "None"}</span>
        </div>
      </div>
    </span>
  );
};

const Changes = ({ form, changes = [] }) => {
  if (!form || Object.keys(form).length === 0) {
    return (
      <div className="text-gray-600 dark:text-gray-400">
        Unable to load form data. Please try again.
      </div>
    );
  }

  const openInNewTab = (url) => {
    window.open(`http://${url}`, "_blank", "noopener,noreferrer");
  };

  const getValue = (value) => (value ? value : "None");

  const getChange = (path, isComposite = false, compositeValue = null) => {
    const relevantChanges = changes.filter((c) => c.path.join(".") === path.join("."));
    if (relevantChanges.length > 0) {
      const latestChange = relevantChanges[relevantChanges.length - 1];
      if (latestChange.from !== latestChange.to) return { to: latestChange.to, from: latestChange.from };
    }

    if (isComposite) {
      const firstNameChange = changes.find((c) => c.path.join(".") === `${path[0]}.FirstName` || c.path.join(".") === `${path.join(".")}.FirstName`);
      const lastNameChange = changes.find((c) => c.path.join(".") === `${path[0]}.LastName` || c.path.join(".") === `${path.join(".")}.LastName`);
      if (firstNameChange || lastNameChange) {
        const from = `${firstNameChange?.from || ""} ${lastNameChange?.from || ""}`.trim();
        const to = compositeValue || `${getValue(form.firstName || form.broker?.firstName)} ${getValue(form.lastName || form.broker?.lastName)}`.trim();
        if (from !== to) return { from: from || null, to };
      }
    }

    return null;
  };

  const hasChanges = (path, isComposite = false) => {
    if (isComposite) {
      return changes.some(
        (c) =>
          c.path.join(".") === `${path[0]}.FirstName` ||
          c.path.join(".") === `${path[0]}.LastName` ||
          c.path.join(".") === `${path.join(".")}.FirstName` ||
          c.path.join(".") === `${path.join(".")}.LastName`
      );
    }
    return changes.some((c) => c.path.join(".") === path.join("."));
  };

  const fields = [
    { label: "Vendor", path: ["Website", "Vendor"], value: form.vendor, colSpan: "1150:col-span-5" },
    { label: "Domain Name", path: ["Website", "Domain Name"], value: form.domainName, colSpan: "1150:col-span-7", isDomain: true },
    { label: "Status", path: ["Website", "Site Status"], value: form.status, colSpan: "1150:col-span-3" },
    { label: "DBA", path: ["Website", "DBA"], value: form.dba, colSpan: "1150:col-span-3" },
    { label: "Agent Search", path: ["Website", "AgentSearch"], value: "", colSpan: "1150:col-span-6" },
    { label: "Start Date", path: ["Website", "Start Date"], value: form.startDate, type: "date", colSpan: "1150:col-span-3" },
    { label: "Term Date", path: ["Website", "Term Date"], value: form.termDate, type: "date", colSpan: "1150:col-span-3" },
    { label: "Member Number", path: ["Website", "Member Number"], value: form.memberNumber, colSpan: "1150:col-span-3" },
    { label: "Product", path: ["Website", "Product"], value: form.product || getValue(changes.find(c => c.path.join(".") === "Website.Product")?.to) || "None", colSpan: "1150:col-span-3" },
    { label: "MLS ID", path: ["Website", "MLS ID"], value: form.mlsId, colSpan: "1150:col-span-4" },
    {
      label: "Full Name",
      path: ["Website"],
      value: `${getValue(form.firstName)} ${getValue(form.lastName)}`,
      isComposite: true,
      colSpan: "1150:col-span-4"
    },
    { label: "Office Name", path: ["Website", "Office"], value: form.office, colSpan: "1150:col-span-4" },
    { label: "MLS Status", path: ["Website", "MLSStatus"], value: form.mlsStatus, colSpan: "1150:col-span-4" },
    { label: "DRE", path: ["Website", "LicenseNumber"], value: form.licenseNumber, colSpan: "1150:col-span-4" },
    {
      label: "Office Broker",
      path: ["Website", "Broker"],
      value: form.broker ? `${getValue(form.broker.firstName)} ${getValue(form.broker.lastName)}` : "None",
      isComposite: true,
      colSpan: "1150:col-span-4"
    },
    { label: "Licensee", path: ["Website", "Licensee"], value: form.licensee, colSpan: "1150:col-span-4" },
    { label: "Broker MLS ID", path: ["Website", "Broker", "MLSID"], value: form.broker?.mlsId, colSpan: "1150:col-span-4" },
    { label: "Broker Office Name", path: ["Website", "Broker", "OfficeName"], value: form.broker?.officeName, colSpan: "1150:col-span-4" }
  ];

  return (
    <div style={{ maxWidth: "1100px", maxHeight: "600px", overflowY: "auto" }}>
      <div className="bg-white dark:bg-stone-900 text-gray-700 dark:text-gray-300 lg:p-6 md:pt-1 md:pl-2">
        <form className="mt-1.5">
          <div className="space-y-4">
            <div className="grid grid-cols-1 1150:grid-cols-12 gap-4">
              {fields.slice(0, 2).map((field, idx) => (
                <div key={idx} className={`col-span-1 ${field.colSpan} ${field.isDomain ? "relative" : ""}`}>
                  <label className="block">
                    <span className="label">
                      <span className={hasChanges(field.path, field.isComposite) ? "text-green-500" : ""}>
                        {field.label}
                      </span>
                      <ChangeIndicator change={getChange(field.path, field.isComposite, field.value)} />
                    </span>
                    <input
                      readOnly
                      className="website-input w-full"
                      type={field.type || "text"}
                      value={getValue(field.value)}
                    />
                    {field.isDomain && form.domainName && (
                      <>
                        <MdOpenInNew
                          className="absolute right-3 top-11 h-4.5 w-4.5 dark:text-gray-300 cursor-pointer transform -translate-y-1/2 1150:block md:hidden"
                          onClick={() => openInNewTab(form.domainName)}
                        />
                        <MdOpenInNew
                          className="absolute right-3 top-11 h-4.5 w-4.5 dark:text-gray-300 cursor-pointer transform -translate-y-1/2 1150:hidden md:block"
                          onClick={() => openInNewTab(form.domainName)}
                        />
                      </>
                    )}
                  </label>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 1150:grid-cols-12 gap-4">
              {fields.slice(2, 5).map((field, idx) => (
                <div key={idx} className={`col-span-1 ${field.colSpan}`}>
                  <label className="block">
                    <span className="label">
                      <span className={hasChanges(field.path, field.isComposite) ? "text-green-500" : ""}>
                        {field.label}
                      </span>
                      <ChangeIndicator change={getChange(field.path, field.isComposite, field.value)} />
                    </span>
                    <input
                      readOnly
                      className="website-input w-full"
                      type={field.type || "text"}
                      value={getValue(field.value)}
                    />
                  </label>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 1150:grid-cols-12 gap-4">
              {fields.slice(5, 9).map((field, idx) => (
                <div key={idx} className={`col-span-1 ${field.colSpan}`}>
                  <label className="block">
                    <span className="label">
                      <span className={hasChanges(field.path, field.isComposite) ? "text-green-500" : ""}>
                        {field.label}
                      </span>
                      <ChangeIndicator change={getChange(field.path, field.isComposite, field.value)} />
                    </span>
                    <input
                      readOnly
                      className="website-input w-full"
                      type={field.type || "text"}
                      value={getValue(field.value)}
                    />
                  </label>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 1150:grid-cols-12 gap-4">
              {fields.slice(9, 12).map((field, idx) => (
                <div key={idx} className={`col-span-1 ${field.colSpan}`}>
                  <label className="block">
                    <span className="label">
                      <span className={hasChanges(field.path, field.isComposite) ? "text-green-500" : ""}>
                        {field.label}
                      </span>
                      <ChangeIndicator change={getChange(field.path, field.isComposite, field.value)} />
                    </span>
                    <input
                      readOnly
                      className="website-input w-full"
                      type={field.type || "text"}
                      value={getValue(field.value)}
                    />
                  </label>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 1150:grid-cols-12 gap-4">
              {fields.slice(12, 18).map((field, idx) => (
                <div key={idx} className={`col-span-1 ${field.colSpan}`}>
                  <label className="block">
                    <span className="label">
                      <span className={hasChanges(field.path, field.isComposite) ? "text-green-500" : ""}>
                        {field.label}
                      </span>
                      <ChangeIndicator change={getChange(field.path, field.isComposite, field.value)} />
                    </span>
                    <input
                      readOnly
                      className="website-input w-full"
                      type={field.type || "text"}
                      value={getValue(field.value)}
                    />
                  </label>
                </div>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Changes;